import React, { useEffect, useMemo, useState } from 'react';
import { Flame, Dumbbell, Timer, Target, ListChecks, HeartPulse, Zap, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAppStore, useTimerStore, useReports, onMaxerState, type MaxerState, type Task } from '@flowfocus/core';
import { SectionLabel } from '../components/ui';

// Fecha LOCAL (no UTC): con toISOString, entre las 00:00 y las 02:00 en Madrid salía el día anterior.
const todayKey = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); };

export function HubPage({ uid }: { uid: string }) {
  const navigate = useNavigate();
  const tasks = useAppStore((s) => s.tasks);
  const dailyGoal = useTimerStore((s) => s.settings.dailyGoal);
  const reports = useReports(uid);
  const [maxer, setMaxer] = useState<MaxerState | null>(null);

  useEffect(() => onMaxerState(uid, setMaxer), [uid]);

  // ── FlowFocus: focus + tasks for today ──────────────────────────────────────
  const focusToday = reports.daily.find((d) => d.date === todayKey())?.sessions ?? 0;

  const { dueToday, overdue } = useMemo(() => {
    const start = startOfToday();
    const endOfToday = start + 86_400_000;
    const pending = tasks.filter((t) => !t.completed);
    return {
      dueToday: pending.filter((t) => t.dueDate && t.dueDate >= start && t.dueDate < endOfToday),
      overdue: pending.filter((t) => t.dueDate && t.dueDate < start),
    };
  }, [tasks]);
  const topTasks = [...overdue, ...dueToday].slice(0, 4);

  // ── MAXER: fitness snapshot ─────────────────────────────────────────────────
  const habits = maxer?.habits ?? {};
  const habitKeys = Object.keys(habits);
  const habitsDone = habitKeys.filter((k) => habits[k]).length;
  const seriesToday = Object.values(maxer?.general ?? {}).reduce((n, arr) => n + (Array.isArray(arr) ? arr.filter(Boolean).length : 0), 0);
  const rehabGoal = maxer?.profile?.rehabDaysGoal ?? 4;

  const hour = new Date().getHours();
  const greeting = hour < 6 ? 'Buenas noches' : hour < 13 ? 'Buenos días' : hour < 21 ? 'Buenas tardes' : 'Buenas noches';
  const name = maxer?.profile?.name || '';
  const dateLabel = (() => { const t = new Date().toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long' }); return t.charAt(0).toUpperCase() + t.slice(1); })();

  return (
    <div style={{ height: '100%', overflowY: 'auto' }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '22px 22px 90px' }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.6px', margin: '0 0 2px' }}>
          {greeting}{name ? `, ${name}` : ''}
        </h1>
        <div style={{ fontSize: 13.5, color: 'var(--text-mut)', marginBottom: 4 }}>{dateLabel}</div>

        {/* ── Enfoque ─────────────────────────────────────────── */}
        <SectionLabel>Enfoque</SectionLabel>
        <div className="card" style={{ borderRadius: 14, padding: 16, display: 'flex', alignItems: 'center', gap: 14 }}>
          <Ring value={focusToday} goal={dailyGoal} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{focusToday} / {dailyGoal} pomodoros</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-mut)' }}>
              {focusToday >= dailyGoal ? '¡Objetivo del día cumplido! 🎉' : `Te faltan ${Math.max(0, dailyGoal - focusToday)} para tu objetivo`}
            </div>
          </div>
          <button className="btn-primary" style={{ padding: '9px 15px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => navigate('/timer')}>
            <Timer size={15} /> Enfocar
          </button>
        </div>

        {/* ── Tareas ──────────────────────────────────────────── */}
        <SectionLabel>Para hoy</SectionLabel>
        <div className="card" style={{ borderRadius: 14, padding: topTasks.length ? '8px 6px' : 16 }}>
          {topTasks.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-mut)', fontSize: 13.5 }}>
              <ListChecks size={16} /> Nada pendiente para hoy. Buen momento para adelantar.
            </div>
          ) : (
            <>
              {topTasks.map((t) => <TaskRow key={t.id} task={t} onClick={() => navigate('/')} />)}
              {(overdue.length + dueToday.length) > topTasks.length && (
                <button style={moreBtn} onClick={() => navigate('/')}>
                  Ver todas ({overdue.length + dueToday.length}) <ArrowRight size={14} />
                </button>
              )}
            </>
          )}
          {overdue.length > 0 && (
            <div style={{ padding: '6px 10px 2px', fontSize: 12, color: 'var(--danger)', fontWeight: 600 }}>
              {overdue.length} atrasada{overdue.length > 1 ? 's' : ''}
            </div>
          )}
        </div>

        {/* ── MAXER ───────────────────────────────────────────── */}
        <SectionLabel>Cuerpo · MAXER</SectionLabel>
        {maxer ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
            <Stat icon={<Flame size={18} color="#F97316" />} label="Racha" value={`${maxer.streak ?? 0} días`} />
            <Stat icon={<Zap size={18} color="#EAB308" />} label="XP hoy" value={`${maxer.xpToday ?? 0}`} sub={`total ${maxer.xp ?? 0}`} />
            <Stat icon={<Target size={18} color="var(--primary)" />} label="Hábitos" value={habitKeys.length ? `${habitsDone}/${habitKeys.length}` : '—'} />
            <Stat icon={<Dumbbell size={18} color="var(--primary)" />} label="Series hoy" value={`${seriesToday}`} />
            <Stat icon={<HeartPulse size={18} color="#EF4444" />} label="Rehab" value={`Sem ${maxer.rehabWeek ?? 1}`} sub={`${maxer.rehabDaysThisWeek ?? 0}/${rehabGoal} días`} />
          </div>
        ) : (
          <div className="card" style={{ borderRadius: 14, padding: 16, fontSize: 13.5, color: 'var(--text-mut)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Dumbbell size={16} /> Sin datos de MAXER todavía. Entra en MAXER con esta misma cuenta y tus entrenos aparecerán aquí.
          </div>
        )}
      </div>
    </div>
  );
}

function Ring({ value, goal }: { value: number; goal: number }) {
  const pct = goal > 0 ? Math.min(1, value / goal) : 0;
  const size = 52, stroke = 5, r = (size - stroke) / 2, c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} style={{ flexShrink: 0, transform: 'rotate(-90deg)' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--border)" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--primary)" strokeWidth={stroke}
        strokeDasharray={c} strokeDashoffset={c * (1 - pct)} strokeLinecap="round" />
    </svg>
  );
}

function Stat({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub?: string }) {
  return (
    <div className="card" style={{ borderRadius: 13, padding: '13px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 7 }}>
        {icon}<span style={{ fontSize: 12.5, color: 'var(--text-mut)', fontWeight: 600 }}>{label}</span>
      </div>
      <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.4px' }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: 'var(--text-mut)', marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

function TaskRow({ task, onClick }: { task: Task; onClick: () => void }) {
  const overdue = task.dueDate && task.dueDate < startOfToday();
  return (
    <button onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', width: '100%', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', borderRadius: 10 }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: overdue ? 'var(--danger)' : 'var(--primary)' }} />
      <span style={{ flex: 1, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.name}</span>
      {task.estimatedPomodoros > 0 && (
        <span style={{ fontSize: 12, color: 'var(--text-mut)' }}>{task.completedPomodoros}/{task.estimatedPomodoros}</span>
      )}
    </button>
  );
}

const moreBtn: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%', padding: '9px', marginTop: 2, background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--primary)' };
